/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";

import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ContentRequests from "@/api/content/content.requests";
import { ROUTES } from "@/config/routes";
import { useGetTimeCodes } from "@/hooks/api/advertisement.hooks";
import { useGetContentDetailPage, useGetContentEpisodes, useGetEpisodeInfo } from "@/hooks/api/content.hooks";

import Spinner from "../helpers/Spinner/Spinner";
import { PlayerUIProvider } from "./PlayerUIContext";
import PlayerUI from "./UI/PlayerUI";
import PlayerAdvertisement from "./adv/PlayerAdvertisement";
import HlsManager from "./hls_manager/hls_manager";
import { IHlsPreparedThumbnail } from "./hls_manager/hls_models";
import { PlayerControlsProvider } from "./hooks/PlayerControlsContext";
import TimeCodeModal from "./modals/TimeCodeModal";
import { useHls } from "./useHls";

const hlsData = (isSerial: boolean, id: number) =>
  `/content/hls-compressed-data-${isSerial ? "episode" : "film"}/${id}?ecb=1`;

type PlayerProps = {
  filmId: number;
  isSerial?: boolean;
  time_code?: number | null;
};

const MainPlayer = ({ filmId, isSerial = false, time_code }: PlayerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [videoLink, setVideoLink] = useState<string>();
  const [showModal, setShowModal] = useState(false);
  const [thumbnail, setThumbnail] = useState<IHlsPreparedThumbnail>();
  const { id, episodeId } = useParams() as { id: string; episodeId: string };
  const { data: advTimeCodes } = useGetTimeCodes(+id);
  const { data: content } = useGetContentDetailPage(+id);
  const { data: episodeInfo } = useGetEpisodeInfo(episodeId ? +episodeId : 0, isSerial);
  const { data: episodesData } = useGetContentEpisodes(episodeInfo?.season_id || 0);

  const accessToken = localStorage.getItem("splay:access") || "";

  enum AdAction {
    play = "play",
    pause = "pause",
    start = "start",
    end = "end",
  }

  const setStatistic = async (action: AdAction) => {
    const title = content.content.title;
    const userId = parseJWT(accessToken)?.payload.user_id;
    const playerAct = action;
    const app = "Web";
    const device = parseJWT(accessToken)?.payload.device;

    const geo = parseJWT(accessToken)?.payload.c_code;
    const params = {
      mh_cont_id: id, // content id
      mh_ep_id: episodeId, // episode id
      mh_cont: title, // content title
      mh_uid: userId, // user id
      mh_page: "web-player-react", // player id
      mh_act: playerAct, // player action
      mh_app: app, // app or web
      mh_eq: device, // device
      mh_geo: geo, // geolocation
    };

    try {
      await axios.get("https://px659.kmetrix.uz/s.gif", { params });
    } catch (error) {
      console.error("Failed to send statistic:", error);
    }
  };

  const cleanup = () => {
    if (videoLink) {
      URL.revokeObjectURL(videoLink);
    }
  };

  const initialize = async () => {
    setVideoLink("");

    cleanup();

    const hlsManager = new HlsManager({
      baseUrl: import.meta.env.VITE_API_URL,
    });

    try {
      if (filmId) {
        const uri = await hlsManager.prepare(hlsData(isSerial, filmId));
        setThumbnail(uri.thumbnails);
        setVideoLink(uri.url);
      }
    } catch (err: any) {
      const resData = err?.response?.data;

      if (resData?.allow === false) {
        const subs = resData.subscriptions.map((item: { id: number }) => item.id);
        navigate(`${ROUTES.SUBS}?allowed=${subs.map((e: number) => `${e}`)}`);
      } else {
        setTimeout(() => {
          initialize();
        }, 500);
      }
    }
  };

  const { videoRef, qualities, currentQuality, setQuality, loading, audioTracks, setAudioTrack, currentAudioTrack } =
    useHls(String(videoLink), time_code || null);

  useEffect(() => {
    initialize();
  }, [location.pathname]);

  const onEndVideo = async () => {
    if (isSerial && episodeId && episodesData && episodeInfo) {
      const episodes = episodesData?.episodes || [];
      const isLastEpisode = episodes[episodes.length - 1]?.id === episodeInfo?.id;

      if (!isLastEpisode) {
        const currentIndex = episodes?.findIndex((ep) => ep.id === episodeInfo?.id);
        const nextEpisode = episodes?.[currentIndex + 1];

        if (nextEpisode) {
          navigate(
            ROUTES.DETAIL.SERIAL_EPISODE?.replace(":id", String(id)).replace(":episodeId", String(nextEpisode?.id)),
          );
          return;
        }
      }
    }

    const res = await ContentRequests.getNextContent(+id);
    navigate(ROUTES.DETAIL.MAIN?.replace(":id", String(res?.[0]?.content_id)));
  };

  useEffect(() => {
    if (time_code) {
      setShowModal(true);
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [time_code]);

  const handleSeek = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = time_code ?? 0;
      videoRef.current.play();
      setStatistic(AdAction.start);
    }
    videoRef.current?.play();
    setShowModal(false);
  };

  const handleFromStart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setStatistic(AdAction.start);
    }
    setShowModal(false);
  };

  if (!videoLink) {
    return (
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <Spinner />
      </div>
    );
  }

  function parseJWT(token: string) {
    try {
      const [header, payload, signature] = token.split(".");
      const decodedHeader = JSON.parse(atob(header));
      const decodedPayload = JSON.parse(atob(payload));
      return { header: decodedHeader, payload: decodedPayload, signature };
    } catch (e) {
      return null;
    }
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 top-0 flex  max-h-[100dvh] w-full items-center justify-center !overflow-hidden bg-black"
      id="player-container"
      style={{ height: "-webkit-fill-available" }}
    >
      <TimeCodeModal open={showModal} onClose={handleFromStart} onConfirm={handleSeek} />

      <video
        ref={videoRef}
        className="h-full w-full object-contain"
        controls={false}
        autoPlay={!time_code || !showModal}
        onEnded={onEndVideo}
        playsInline
      />

      <PlayerControlsProvider>
        <PlayerUIProvider loading={loading}>
          {!showModal && advTimeCodes && advTimeCodes?.length > 0 && (
            <PlayerAdvertisement videoRef={videoRef} advTimeCodes={advTimeCodes} />
          )}
          <PlayerUI
            loading={loading}
            videoRef={videoRef}
            thumbnail={thumbnail || null}
            qualities={qualities}
            setQuality={setQuality}
            currentQuality={currentQuality}
            audioTracks={audioTracks}
            setAudioTrack={setAudioTrack}
            currentAudioTrack={currentAudioTrack}
          />
        </PlayerUIProvider>
      </PlayerControlsProvider>
    </div>
  );
};

export default MainPlayer;
